import { gql, useQuery } from "@apollo/client";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  Icon,
  MenuItem,
  Radio,
  Select,
  useMediaQuery,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import CompactPagination from "components/CompactPagination";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

const PRODUCTOS_VOUCHER_QUERY = gql`
  query productosVoucherSelector($filter: JSON, $search: String, $page: Int!, $limit: Int!) {
    paginationInfo {
      pages
      total
    }
    productos(
      filter: $filter
      search: $search
      pagination: { page: $page, limit: $limit }
      order: { field: "nombre", order: ASC }
    ) {
      id
      nombre
      disponibilidad
      esIngrediente
      categoriaProducto {
        id
        categoria
      }
    }
  }
`;

const PAGE_SIZE = 10;

function ProductoVoucherSelectorDialog({
  open,
  categorias,
  selectedProducto,
  onCancel,
  onConfirm,
}) {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [idCategoria, setIdCategoria] = useState("");
  const [page, setPage] = useState(1);
  const [pendingProducto, setPendingProducto] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setDebouncedSearch("");
    setIdCategoria("");
    setPage(1);
    setPendingProducto(selectedProducto || null);
  }, [open, selectedProducto]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const filter = useMemo(
    () => ({
      esIngrediente: false,
      ...(idCategoria ? { idCategoria: Number(idCategoria) } : {}),
    }),
    [idCategoria]
  );

  const { data, loading, error } = useQuery(PRODUCTOS_VOUCHER_QUERY, {
    variables: { filter, search: debouncedSearch || null, page, limit: PAGE_SIZE },
    skip: !open,
    fetchPolicy: "network-only",
  });

  const productos = data?.productos || [];
  const pagination = data?.paginationInfo || { pages: 0, total: 0 };
  const totalPages = Number(pagination.pages || 0);
  const totalProducts = Number(pagination.total || 0);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullScreen={fullScreen}
      maxWidth={false}
      aria-labelledby="producto-voucher-selector-title"
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(71, 80, 93, 0.56)",
          backdropFilter: "blur(2px)",
        },
      }}
      PaperProps={{
        sx: {
          width: fullScreen ? "100%" : "calc(100% - 40px)",
          height: fullScreen ? "100%" : "82vh",
          minHeight: fullScreen ? "100%" : "500px",
          maxWidth: fullScreen ? "none" : "880px",
          maxHeight: fullScreen ? "none" : "620px",
          m: fullScreen ? 0 : 2.5,
          borderRadius: fullScreen ? 0 : "14px",
          backgroundImage: "none",
          boxShadow: "0 18px 36px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          p: { xs: 2, sm: 2.5 },
          "&:first-of-type": { pt: { xs: 2, sm: 2.5 } },
        }}
      >
        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
          <SoftBox pr={2}>
            <SoftTypography
              id="producto-voucher-selector-title"
              component="h2"
              variant="h4"
              fontWeight="bold"
              sx={{
                color: "#0f1d3d",
                fontSize: { xs: "1.45rem", sm: "1.55rem" },
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
              }}
            >
              Seleccionar producto
            </SoftTypography>
            <SoftTypography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "#71809f",
                fontSize: { xs: "0.78rem", sm: "0.82rem" },
                lineHeight: 1.4,
              }}
            >
              Se muestran todos los productos que no son ingredientes.
            </SoftTypography>
          </SoftBox>

          <SoftButton
            color="white"
            circular
            iconOnly
            onClick={onCancel}
            aria-label="Cerrar selector de productos"
            sx={{
              flex: "0 0 auto",
              width: { xs: 38, sm: 36 },
              minWidth: { xs: 38, sm: 36 },
              height: { xs: 38, sm: 36 },
              minHeight: { xs: 38, sm: 36 },
              p: 0,
              color: "#122241",
              border: "1px solid #e0e6ef",
              boxShadow: "0 3px 7px rgba(15, 23, 42, 0.1)",
              "&:hover": { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" },
            }}
          >
            <Icon sx={{ fontSize: "1.2rem !important" }}>close</Icon>
          </SoftButton>
        </SoftBox>

        <SoftBox
          display="grid"
          gap={{ xs: 1, sm: 1.25 }}
          mt={{ xs: 2, sm: 1.5 }}
          sx={{ gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1.8fr) minmax(240px, 1fr)" } }}
        >
          <SoftInput
            id="producto-voucher-search"
            size="medium"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrar por nombre"
            icon={{ component: "search", direction: "left" }}
            inputProps={{ "aria-label": "Filtrar productos por nombre" }}
            sx={{
              "& .MuiInputBase-input": { fontSize: "0.85rem" },
            }}
          />

          <Select
            id="producto-voucher-category-filter"
            fullWidth
            displayEmpty
            value={idCategoria}
            onChange={(event) => {
              setIdCategoria(event.target.value);
              setPage(1);
            }}
            inputProps={{ "aria-label": "Filtrar productos por categoría" }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  maxHeight: 300,
                  borderRadius: "10px",
                  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.16)",
                },
              },
            }}
            sx={{
              height: 42,
              color: "#172746",
              fontSize: "0.85rem",
              borderRadius: "10px",
              backgroundColor: "#fff",
              "& .MuiSelect-select": { display: "flex", alignItems: "center", px: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2563eb",
                borderWidth: "1px",
                boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
              },
              "& .MuiSelect-icon": { right: 14, color: "#253451" },
            }}
          >
            <MenuItem value="">Todas las categorías</MenuItem>
            {categorias.map((categoria) => (
              <MenuItem key={categoria.id} value={categoria.id}>
                {categoria.categoria}
              </MenuItem>
            ))}
          </Select>
        </SoftBox>

        <SoftBox
          mt={{ xs: 1.5, sm: 1.25 }}
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            minHeight: 0,
            border: "1px solid #d7dee9",
            borderRadius: "11px",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <SoftBox
            display="grid"
            alignItems="center"
            sx={{
              flex: "0 0 auto",
              minHeight: { xs: 44, sm: 42 },
              gridTemplateColumns: {
                xs: "52px minmax(0, 1fr)",
                md: "66px minmax(0, 1.45fr) minmax(160px, 1fr) 150px",
              },
              background: "linear-gradient(180deg, #fbfcfe 0%, #f7f9fc 100%)",
              borderBottom: "1px solid #dfe5ed",
            }}
          >
            <SoftBox />
            <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#344565", px: 1.5 }}>
              Producto
            </SoftTypography>
            <SoftTypography
              variant="button"
              fontWeight="bold"
              sx={{ display: { xs: "none", md: "block" }, color: "#344565", px: 1.5 }}
            >
              Categoría
            </SoftTypography>
            <SoftTypography
              variant="button"
              fontWeight="bold"
              sx={{ display: { xs: "none", md: "block" }, color: "#344565", px: 1.5 }}
            >
              Estado
            </SoftTypography>
          </SoftBox>

          <SoftBox
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              scrollbarColor: "#b8c3d4 #f1f4f8",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: "10px" },
              "&::-webkit-scrollbar-track": { background: "#f1f4f8", borderRadius: "999px" },
              "&::-webkit-scrollbar-thumb": {
                background: "#b8c3d4",
                border: "2px solid #f1f4f8",
                borderRadius: "999px",
              },
            }}
          >
            {loading ? (
              <SoftBox minHeight="220px" height="100%" display="grid" sx={{ placeItems: "center" }}>
                <SoftBox display="flex" alignItems="center" gap={1.25}>
                  <CircularProgress size={22} sx={{ color: "#2563eb" }} />
                  <SoftTypography variant="button" fontWeight="regular" color="secondary">
                    Cargando productos…
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            ) : error ? (
              <SoftBox
                minHeight="220px"
                height="100%"
                display="grid"
                p={2}
                sx={{ placeItems: "center" }}
              >
                <SoftTypography variant="button" color="error">
                  No se pudieron cargar los productos.
                </SoftTypography>
              </SoftBox>
            ) : productos.length ? (
              productos.map((producto) => {
                const checked = Number(pendingProducto?.id) === Number(producto.id);
                const available = producto.disponibilidad !== false;

                return (
                  <SoftBox
                    key={producto.id}
                    component="label"
                    display="grid"
                    alignItems="center"
                    width="100%"
                    sx={{
                      minHeight: { xs: 56, sm: 47 },
                      gridTemplateColumns: {
                        xs: "52px minmax(0, 1fr)",
                        md: "66px minmax(0, 1.45fr) minmax(160px, 1fr) 150px",
                      },
                      borderBottom: "1px solid #e1e6ed",
                      backgroundColor: checked ? "#eef4ff" : "#fff",
                      cursor: "pointer",
                      transition: "background-color 180ms ease",
                      "&:hover": { backgroundColor: checked ? "#e8f0ff" : "#f8fafc" },
                      "&:focus-within": { outline: "2px solid #2563eb", outlineOffset: -2 },
                      "&:last-of-type": { borderBottom: 0 },
                    }}
                  >
                    <SoftBox display="grid" sx={{ placeItems: "center" }}>
                      <Radio
                        checked={checked}
                        onChange={() => setPendingProducto(producto)}
                        value={producto.id}
                        name="producto-voucher-selection"
                        inputProps={{ "aria-label": `Seleccionar ${producto.nombre}` }}
                        sx={{
                          width: { xs: 23, sm: 22 },
                          height: { xs: 23, sm: 22 },
                          m: 0,
                          p: 0,
                          border: "1.5px solid #b7c4d7",
                          backgroundColor: "#fff",
                          backgroundImage: "none",
                          "& .MuiSvgIcon-root": { display: "none" },
                          "&.Mui-checked": {
                            borderColor: "#2563eb",
                            backgroundColor: "#2563eb",
                            backgroundImage: "radial-gradient(circle, #fff 0 24%, transparent 27%)",
                          },
                          "&:hover": { backgroundColor: checked ? "#2563eb" : "#fff" },
                        }}
                      />
                    </SoftBox>

                    <SoftBox minWidth={0} px={1.5} py={0.75}>
                      <SoftTypography
                        variant="body2"
                        title={producto.nombre}
                        fontWeight={checked ? "medium" : "regular"}
                        sx={{
                          overflow: "hidden",
                          color: "#172746",
                          fontSize: { xs: "0.86rem", sm: "0.85rem" },
                          lineHeight: 1.3,
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {producto.nombre}
                      </SoftTypography>
                      <SoftBox
                        display={{ xs: "flex", md: "none" }}
                        alignItems="center"
                        gap={0.75}
                        mt={0.25}
                      >
                        <SoftTypography
                          variant="caption"
                          color="secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {producto?.categoriaProducto?.categoria || "Sin categoría"}
                        </SoftTypography>
                        <SoftBadge
                          color={available ? "success" : "secondary"}
                          variant="contained"
                          size="xs"
                          badgeContent={available ? "Disponible" : "No disponible"}
                          container
                        />
                      </SoftBox>
                    </SoftBox>

                    <SoftTypography
                      variant="button"
                      fontWeight="regular"
                      sx={{ display: { xs: "none", md: "block" }, color: "#7888a7", px: 1.5 }}
                    >
                      {producto?.categoriaProducto?.categoria || "Sin categoría"}
                    </SoftTypography>

                    <SoftBox sx={{ display: { xs: "none", md: "block" }, px: 1.5 }}>
                      <SoftBadge
                        color={available ? "success" : "secondary"}
                        variant="contained"
                        size="xs"
                        badgeContent={available ? "Disponible" : "No disponible"}
                        container
                      />
                    </SoftBox>
                  </SoftBox>
                );
              })
            ) : (
              <SoftBox
                minHeight="220px"
                height="100%"
                display="grid"
                p={2}
                sx={{ placeItems: "center" }}
              >
                <SoftTypography variant="button" fontWeight="regular" color="secondary">
                  No se encontraron productos.
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>

        <SoftBox mt={{ xs: 1.25, sm: 1 }} sx={{ flex: "0 0 auto" }}>
          {totalPages > 1 ? (
            <CompactPagination
              page={page}
              pages={totalPages}
              length={productos.length}
              total={totalProducts}
              onChange={setPage}
              variant="selector"
              noCard
            />
          ) : (
            <SoftTypography
              variant="button"
              fontWeight="regular"
              sx={{ color: "#71809f", fontSize: "0.8rem" }}
            >
              {`${totalProducts} productos`}
            </SoftTypography>
          )}
        </SoftBox>

        <SoftBox
          mt={{ xs: 1.5, sm: 1.25 }}
          display="flex"
          justifyContent="flex-end"
          gap={{ xs: 1, sm: 1.5 }}
          sx={{ flex: "0 0 auto" }}
        >
          <SoftButton
            variant="outlined"
            color="secondary"
            circular
            onClick={onCancel}
            sx={{
              width: { xs: "50%", sm: 120 },
              height: 38,
              borderColor: "#d5dde8",
              borderRadius: "10px",
              fontSize: "0.7rem",
            }}
          >
            Cancelar
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="info"
            circular
            disabled={!pendingProducto}
            onClick={() => onConfirm(pendingProducto)}
            sx={{
              width: { xs: "50%", sm: 150 },
              height: 38,
              borderRadius: "10px",
              boxShadow: pendingProducto ? "0 6px 13px rgba(37, 99, 235, 0.28)" : "none",
              fontSize: "0.7rem",
            }}
          >
            Seleccionar
          </SoftButton>
        </SoftBox>
      </DialogContent>
    </Dialog>
  );
}

ProductoVoucherSelectorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  categorias: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      categoria: PropTypes.string,
    })
  ).isRequired,
  selectedProducto: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    nombre: PropTypes.string,
  }),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ProductoVoucherSelectorDialog.defaultProps = { selectedProducto: null };

export default ProductoVoucherSelectorDialog;
